/**
 * InventoryValuationScreen — stock valuation (at cost and retail) as of any
 * chosen date, by rewinding movements from live stock. Month/year-end closing
 * stock for the books.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { toDateInputValue, fromDateInputValue, startOfNextDay } from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadInventoryValuation } from '@/reports/valuation/inventoryValuationDataSource';
import { buildInventoryValuation, type InventoryValuation } from '@/reports/valuation/inventoryValuation';

// ─── Component ───────────────────────────────────────────────────────────────

export function InventoryValuationScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [asOfInput, setAsOfInput] = useState(toDateInputValue(new Date()));
  const [data, setData]       = useState<InventoryValuation | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // "As of <date>" means end of that day, so include the whole day's movements.
  const asOfInstant = useCallback(() => startOfNextDay(fromDateInputValue(asOfInput)), [asOfInput]);

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      setData(buildInventoryValuation(await loadInventoryValuation(activeStoreId, asOfInstant())));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, asOfInstant]);

  useEffect(() => { void load(); }, [load]);

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.rows, [
        { header: 'Product', value: (r) => r.name },
        { header: 'Category', value: (r) => r.category },
        { header: 'Quantity', value: (r) => r.quantity },
        { header: 'Cost value', value: (r) => r.costValue },
        { header: 'Retail value', value: (r) => r.retailValue },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `InventoryValuation_${asOfInput}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      await pdf.saveFromHtml({
        html: buildValuationHtml(data, asOfInput),
        defaultFileName: `InventoryValuation_${asOfInput}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body text-fg-tertiary">Value as of</span>
        <input type="date" value={asOfInput} max={toDateInputValue(new Date())}
          onChange={(e) => setAsOfInput(e.target.value)}
          className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.rows.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">No stock held on this date.</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Stock value (cost)" value={formatCurrency(data.totalCost)} accent />
            <KPI label="Stock value (retail)" value={formatCurrency(data.totalRetail)} />
            <KPI label="SKUs" value={String(data.skuCount)} />
            <KPI label="Units" value={String(data.totalUnits)} />
          </div>

          {/* By category */}
          {data.byCategory.length > 0 && (
            <Card title="By category">
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Category</th>
                    <th className="pb-1 text-right font-normal">Units</th>
                    <th className="pb-1 text-right font-normal">Cost value</th>
                    <th className="pb-1 text-right font-normal">Retail value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byCategory.map((c) => (
                    <tr key={c.category} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 text-fg-primary">{c.category}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{c.quantity}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-primary">{formatCurrency(c.costValue)}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{formatCurrency(c.retailValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Products */}
          <Card title="By product (most valuable first)">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Product</th>
                    <th className="pb-1 text-right font-normal">Qty</th>
                    <th className="pb-1 text-right font-normal">Cost value</th>
                    <th className="pb-1 text-right font-normal">Retail value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.productId} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5">
                        <div className="text-fg-primary">{r.name}</div>
                        <div className="text-label text-fg-tertiary">{r.category}</div>
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{r.quantity}</td>
                      <td className="py-1.5 text-right font-medium tabular-nums text-fg-primary">{formatCurrency(r.costValue)}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{formatCurrency(r.retailValue)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-stroke-hairline/20 font-medium">
                    <td className="pt-2 text-fg-primary">Total</td>
                    <td className="pt-2 text-right tabular-nums text-fg-primary">{data.totalUnits}</td>
                    <td className="pt-2 text-right tabular-nums text-accent-ink">{formatCurrency(data.totalCost)}</td>
                    <td className="pt-2 text-right tabular-nums text-fg-secondary">{formatCurrency(data.totalRetail)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', accent ? 'text-accent-ink' : 'text-fg-primary')}>{value}</div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildValuationHtml(d: InventoryValuation, asOfDate: string): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

  const catRows = d.byCategory.map((c) =>
    `<tr><td>${esc(c.category)}</td><td style="text-align:right">${c.quantity}</td><td style="text-align:right">${fmt(c.costValue)}</td><td style="text-align:right">${fmt(c.retailValue)}</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}h2{font-size:13px;margin-top:22px;text-transform:uppercase;color:#666}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}th,td{padding:5px 8px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:first-child,td:first-child{text-align:left}
tfoot td{border-top:2px solid #333;font-weight:700}</style></head>
<body>
<h1>Inventory Valuation</h1>
<p style="color:#888">As of ${new Date(asOfDate).toLocaleDateString('en-IN')} · ${d.skuCount} SKUs · ${d.totalUnits} units · Cost ${fmt(d.totalCost)} · Retail ${fmt(d.totalRetail)}</p>
<h2>By category</h2>
<table><thead><tr><th>Category</th><th>Units</th><th>Cost value</th><th>Retail value</th></tr></thead><tbody>${catRows}</tbody>
<tfoot><tr><td>Total</td><td style="text-align:right">${d.totalUnits}</td><td style="text-align:right">${fmt(d.totalCost)}</td><td style="text-align:right">${fmt(d.totalRetail)}</td></tr></tfoot></table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}. Cost = purchase price; quantities rewound from live stock.</p>
</body></html>`;
}
